import { CANVAS_SIZE } from "@/lib/constants/slides";
import { convertElementsToPixels } from "@/lib/utils/elementConversion";
import { backgroundColors } from "@ai-teach/canvas-styles";
import type { LineElement, QuestionAnswersMap, SlideElement } from "@ai-teach/types";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { StaticElement } from "../slides/editor/StaticElement";
import { DraggableLine } from "../slides/editor/renderers/DraggableLine";
import { REPLAY_HEIGHT } from "./readOnlyStyles";

const { width: CANVAS_WIDTH, height: CANVAS_HEIGHT } = CANVAS_SIZE.freeform.size;

interface CanvasReplayProps {
  elements: SlideElement[];
  answers: QuestionAnswersMap;
}

/** Locks exactly what the student answered, in the same keys the live canvas locks. */
function frozenBlanksFromAnswers(
  elements: SlideElement[],
  answers: QuestionAnswersMap
): Set<string> {
  const frozen = new Set<string>();
  for (const element of elements) {
    const answer = answers[element.id];
    if (!answer) continue;
    if (answer.type === "fillBlanks") {
      for (const blankId of Object.keys(answer.answers)) frozen.add(`${element.id}-${blankId}`);
      continue;
    }
    frozen.add(element.id);
  }
  return frozen;
}

/**
 * A stored canvas play, drawn with the live renderers: every element in place, the student's
 * answers filled in, everything frozen so the graded colours and correct answers show at once.
 * Laid out at the authoring canvas size and scaled down, the way every other web preview does it.
 */
export function CanvasReplay({ elements, answers }: CanvasReplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      setScale(width > 0 ? Math.min(width / CANVAS_WIDTH, REPLAY_HEIGHT / CANVAS_HEIGHT, 1) : 0);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const pixelElements = useMemo(
    () => convertElementsToPixels(elements, "freeformCanvas"),
    [elements]
  );
  const frozenBlanks = useMemo(
    () => frozenBlanksFromAnswers(elements, answers),
    [elements, answers]
  );

  return (
    /* The outer box measures the room available; the scaled canvas is centred inside it. */
    <div ref={containerRef} style={{ width: "100%" }}>
      <div
        style={{
          width: CANVAS_WIDTH * scale,
          height: CANVAS_HEIGHT * scale,
          margin: "0 auto",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            position: "absolute",
            top: 0,
            left: 0,
            overflow: "hidden",
            backgroundColor: backgroundColors.canvasBg,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {pixelElements
            .filter((element) => element.type !== "line")
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
            .map((element) => (
              <StaticElement
                key={element.id}
                element={element}
                answer={answers[element.id]}
                frozenBlanks={frozenBlanks}
                isFrozen
                canvasHeight={CANVAS_HEIGHT}
              />
            ))}

          {/* A line is draggable by construction, and dragging one here would write to the editor's
              own slide state — the only element that has to be held inert. */}
          <div style={{ pointerEvents: "none" }}>
            {pixelElements
              .filter((element) => element.type === "line")
              .map((element) => (
                <DraggableLine key={element.id} element={element as LineElement} scale={scale} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
